import { ChevronDown, Download, Search } from 'lucide-react';
import type { DerivedRegistrationStatus, Fase, InscritoSortKey } from './inscritosVocabulary';
import { statusLabel } from './inscritosVocabulary';

// "Inscrito" e "Não iniciou" são o mesmo `derivedStatus: "nao_iniciou"`, lido
// diferente por fase (inscritosVocabulary.ts, `isInscrito`) — mas este filtro
// oferece os cinco rótulos sempre, a mesma escolha que a story "Todos" de
// InscritoStatusBadge já fez para o selo da linha. Por isso o filtro usa um
// valor próprio ("inscrito") em vez de reaproveitar "nao_iniciou" para as
// duas opções: um <select> com dois <option> de mesmo value não consegue
// manter os dois rótulos distinguíveis depois de selecionados.
export type InscritosStatusFilterValue = 'todos' | 'inscrito' | DerivedRegistrationStatus;

export interface InscritosToolbarProps {
  /** Decide se "Nota" entra na ordenação — proposal.md, Suposição 7. */
  fase: Fase;
  search: string;
  onSearchChange: (value: string) => void;
  status: InscritosStatusFilterValue;
  onStatusChange: (value: InscritosStatusFilterValue) => void;
  /** Controlado pelo pai; "nome" é o padrão quando omitido. */
  sort?: InscritoSortKey;
  onSortChange: (value: InscritoSortKey) => void;
  /** O pai baixa o CSV (lista filtrada inteira) — este componente não o monta. */
  onExportCsv: () => void;
}

const STATUS_OPTIONS: Array<{ value: InscritosStatusFilterValue; label: string }> = [
  { value: 'todos', label: 'Todos os status' },
  { value: 'inscrito', label: statusLabel('nao_iniciou', 'antes') },
  { value: 'concluido', label: statusLabel('concluido', 'depois') },
  { value: 'incompleto', label: statusLabel('incompleto', 'depois') },
  { value: 'nao_iniciou', label: statusLabel('nao_iniciou', 'depois') },
  { value: 'cancelado', label: statusLabel('cancelado', 'depois') },
];

const BASE_SORT_OPTIONS: Array<{ value: InscritoSortKey; label: string }> = [
  { value: 'nome', label: 'Nome' },
  { value: 'data', label: 'Data de inscrição' },
];

const NOTA_SORT_OPTION = { value: 'nota' as const, label: 'Nota' };

// O estado "não iniciado" não tem nota para ordenar — Nota só existe a partir
// do início do simulado, quando os acertos já existem (proposal.md, Suposição
// 7). `durante` trata a janela como `depois` (design.md de
// `simulado-cancelamento-efetivo`, Decisão 1) — por isso o corte é
// `fase !== 'antes'`, não uma comparação com `'depois'`.
function sortOptionsFor(fase: Fase) {
  return fase !== 'antes' ? [...BASE_SORT_OPTIONS, NOTA_SORT_OPTION] : BASE_SORT_OPTIONS;
}

const FIELD_WRAPPER =
  'relative flex h-9 items-center gap-2 rounded-input border border-edge bg-surface px-3';
const SELECT_CLASS =
  'h-full appearance-none bg-transparent pr-5 text-sm text-ink-secondary outline-none';

export function InscritosToolbar({
  fase,
  search,
  onSearchChange,
  status,
  onStatusChange,
  sort = 'nome',
  onSortChange,
  onExportCsv,
}: InscritosToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex h-9 min-w-[240px] flex-1 items-center gap-2 rounded-input border border-edge bg-surface px-3">
        <Search size={15} className="shrink-0 text-ink-muted" aria-hidden />
        <input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          aria-label="Buscar por nome ou e-mail"
          placeholder="Buscar por nome ou e-mail"
          className="h-full w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-muted"
        />
      </label>

      <div className={FIELD_WRAPPER}>
        <select
          value={status}
          onChange={(event) => onStatusChange(event.target.value as InscritosStatusFilterValue)}
          aria-label="Filtrar por status"
          className={SELECT_CLASS}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-3 text-ink-muted" aria-hidden />
      </div>

      <div className={FIELD_WRAPPER}>
        <select
          value={sort}
          onChange={(event) => onSortChange(event.target.value as InscritoSortKey)}
          aria-label="Ordenar por"
          className={SELECT_CLASS}
        >
          {sortOptionsFor(fase).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown size={14} className="pointer-events-none absolute right-3 text-ink-muted" aria-hidden />
      </div>

      <button
        type="button"
        onClick={onExportCsv}
        className="flex h-9 items-center gap-2 rounded-input border border-edge-strong bg-surface px-3 text-sm font-medium text-ink hover:bg-surface-hover"
      >
        <Download size={15} aria-hidden />
        Exportar CSV
      </button>
    </div>
  );
}

export default InscritosToolbar;
